import {
  PRESENCE_INTERVAL_MS,
  type PresenceEntry
} from "shared/utils/live-view/protocol";

/** Presence entry plus when this viewer last heard about it (local clock). */
export interface PresenceRecord extends PresenceEntry {
  receivedAt: number;
}

export type PresenceMap = ReadonlyMap<number, PresenceRecord>;

/**
 * The applicant re-announces every 20 s; after three missed beats we stop
 * trusting the entry even if `presence:left` never came (e.g. a server
 * restart). Measured on the viewer's clock so server clock skew can't hide
 * or keep a chip.
 */
export const PRESENCE_STALE_MS = PRESENCE_INTERVAL_MS * 3;

export const EMPTY_PRESENCE: PresenceMap = new Map();

export const isPresenceEntry = (value: unknown): value is PresenceEntry => {
  if (!value || typeof value !== "object") return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.application_id === "number" &&
    typeof entry.applicant_id === "number" &&
    typeof entry.applicant_name === "string" &&
    typeof entry.step === "string"
  );
};

export const presenceFromSnapshot = (
  entries: readonly unknown[],
  now: number
): PresenceMap =>
  new Map(
    entries
      .filter(isPresenceEntry)
      .map((entry) => [entry.applicant_id, { ...entry, receivedAt: now }])
  );

export const upsertPresence = (
  map: PresenceMap,
  entry: PresenceEntry,
  now: number
): PresenceMap => {
  const next = new Map(map);
  next.set(entry.applicant_id, { ...entry, receivedAt: now });
  return next;
};

export const removePresence = (
  map: PresenceMap,
  applicantId: number
): PresenceMap => {
  if (!map.has(applicantId)) return map;
  const next = new Map(map);
  next.delete(applicantId);
  return next;
};

/** Drops stale entries; returns the same map when nothing changed. */
export const pruneStalePresence = (
  map: PresenceMap,
  now: number,
  maxAgeMs: number = PRESENCE_STALE_MS
): PresenceMap => {
  const fresh = [...map.entries()].filter(
    ([, record]) => now - record.receivedAt <= maxAgeMs
  );
  return fresh.length === map.size ? map : new Map(fresh);
};
